import { DataPeriod } from '@prisma/client';
import { UserReportResponse } from '@zond/api-interfaces';
import {
  addDays,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isValid,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { BarChart3 } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Bar,
  BarChart,
  BarSeries,
  ChartTooltip,
  Gridline,
  GridlineSeries,
  LinearXAxis,
  LinearXAxisTickLabel,
  LinearXAxisTickSeries,
  LinearYAxis,
  LinearYAxisTickLabel,
  LinearYAxisTickSeries,
  TooltipArea,
} from 'reaviz';
import { CHART_COLORS } from '../modules/reports/constants/chart-colors';

interface TrackedTimeChartToolProps {
  data?: UserReportResponse;
  isLoading?: boolean;
  width?: string | number;
  height?: number;
}

interface ChartDataPoint {
  key: Date;
  data: number;
}

export const TrackedTimeChartTool: React.FC<TrackedTimeChartToolProps> = ({
  data,
  isLoading = false,
  width,
  height,
}) => {
  // Get period from URL
  const location = useLocation();
  const [period, setPeriod] = useState<DataPeriod>(DataPeriod.Daily);
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(new Date());

  // Ref for chart container and state for its dimensions
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Update width function
  const updateWidth = () => {
    if (chartContainerRef.current) {
      const newWidth = chartContainerRef.current.clientWidth;
      if (newWidth > 0) {
        setContainerWidth(newWidth);
      }
    }
  };

  // Track container size changes and update on period/data changes
  useEffect(() => {
    if (!chartContainerRef.current) return;

    // Initialize width
    updateWidth();

    // Create ResizeObserver to track size changes
    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(chartContainerRef.current);

    // Update width on window resize
    window.addEventListener('resize', updateWidth);

    // Cleanup on unmount
    return () => {
      if (chartContainerRef.current) {
        resizeObserver.unobserve(chartContainerRef.current);
      }
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, [period, data]);

  // Update period and dates when URL changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    console.log('URL Search Params:', location.search);

    // Get period from range parameter
    const rangeParam = searchParams.get('range');
    let periodValue: DataPeriod = DataPeriod.Daily;

    if (rangeParam && Object.values(DataPeriod).includes(rangeParam as DataPeriod)) {
      periodValue = rangeParam as DataPeriod;
    }

    console.log('Selected period:', periodValue);
    setPeriod(periodValue);

    // Set start and end dates based on period
    const now = new Date();
    let start: Date;
    let end: Date = now;

    // Check if there's a date parameter in URL
    const dateParam = searchParams.get('date');
    const dateObj = dateParam ? new Date(dateParam) : now;
    const validDate = isValid(dateObj) ? dateObj : now;

    switch (periodValue) {
      case DataPeriod.Monthly:
        start = startOfMonth(validDate);
        end = endOfMonth(validDate);
        break;
      case DataPeriod.Weekly:
        start = startOfWeek(validDate, { weekStartsOn: 1 }); // 1 = Monday
        end = endOfWeek(validDate, { weekStartsOn: 1 });
        break;
      case DataPeriod.Daily:
      default:
        start = startOfDay(validDate);
        end = endOfDay(validDate);
        break;
    }

    setStartDate(start);
    setEndDate(end);
  }, [location.search]);

  // Generate time marks for X axis
  const generateTimeMarks = useMemo(() => {
    const marks: Date[] = [];
    let currentDate = new Date(startDate);
    const end = new Date(endDate);

    switch (period) {
      case DataPeriod.Monthly:
      case DataPeriod.Weekly:
        // For month and week - marks for each day
        while (currentDate <= end) {
          marks.push(new Date(currentDate));
          currentDate = addDays(currentDate, 1);
        }
        break;
      case DataPeriod.Daily:
      default:
        return [];
    }

    return marks;
  }, [startDate, endDate, period]);

  // Process data for chart
  const chartData = useMemo(() => {
    console.log('Processing chart data:', {
      hasSessions: !!data?.sessions?.length,
      period,
      sessionsCount: data?.sessions?.length,
      startDate,
      endDate,
    });

    if (!data?.sessions?.length || period === DataPeriod.Daily) {
      return [];
    }

    // Create map to store aggregated data by day
    const timeByDay = new Map<number, number>();

    // Initialize all days in the range with zero values
    let currentDate = new Date(startDate);
    const end = new Date(endDate);
    while (currentDate <= end) {
      timeByDay.set(startOfDay(currentDate).getTime(), 0);
      currentDate = addDays(currentDate, 1);
    }

    // Process session data
    data.sessions.forEach((session) => {
      const startSession = new Date(session.createdAt);
      const endSession = new Date(session.EndSession?.[0]?.createdAt ?? session.createdAt);

      // Get the day key (start of day timestamp)
      const dayKey = startOfDay(startSession).getTime();

      // Calculate session duration in milliseconds
      const duration = endSession.getTime() - startSession.getTime();

      // Add to the map (days already initialized with 0)
      timeByDay.set(dayKey, (timeByDay.get(dayKey) || 0) + duration);
    });

    // Convert to the format needed for the chart
    const result = Array.from(timeByDay.entries())
      .sort(([a], [b]) => a - b) // Sort by date ascending
      .map(([dayKey, duration]) => ({
        key: new Date(dayKey),
        data: duration / (1000 * 60 * 60), // Convert to hours
      }));

    console.log('Processed chart data:', {
      result,
      timeMarks: generateTimeMarks,
      domain: [startDate.getTime(), endDate.getTime()],
    });
    return result;
  }, [data, period, startDate, endDate, generateTimeMarks]);

  // Format time label for X axis
  const formatXAxisTime = (value: any): string => {
    let date: Date;

    if (value instanceof Date) {
      date = value;
    } else if (typeof value === 'number') {
      date = new Date(value);
    } else {
      console.error('Invalid data type for X axis formatting:', value);
      return '';
    }

    if (isNaN(date.getTime())) {
      console.error('Invalid date:', value);
      return '';
    }

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');

    switch (period) {
      case DataPeriod.Monthly:
        return `${day}.${month}`;
      case DataPeriod.Weekly: {
        const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const weekday = weekdays[date.getDay()];
        return `${weekday} ${day}.${month}`;
      }
      default:
        return '';
    }
  };

  // Format value for Y axis (hours)
  const formatYAxisValue = (value: number): string => {
    return `${Math.round(value)}h`;
  };

  // Format hours for tooltip
  const formatTooltipTime = (hours: number): string => {
    const totalMinutes = Math.round(hours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;

    if (h === 0) {
      return `${m} min`;
    }

    return `${h} h ${m} min`;
  };

  // If data is loading
  if (isLoading) {
    return (
      <div className="w-full h-[350px] flex items-center justify-center rounded">
        <div className="text-gray-500">Loading data...</div>
      </div>
    );
  }

  // If no data or period is Daily
  if (!data?.sessions?.length || period === DataPeriod.Daily) {
    return (
      <div className="w-full h-[350px] flex flex-col items-center justify-center text-center">
        <div className="h-12 flex items-center justify-center border rounded-lg p-2 mb-4">
          <BarChart3 className="h-6 w-6 text-muted-foreground" />
        </div>
        <h3 className="text-xl font-semibold mb-2">No Tracked Time Data</h3>
        <p className="text-muted-foreground">
          Tracked time data will appear here once
          <br />
          users start tracking their time.
        </p>
      </div>
    );
  }

  // Render component
  return (
    <div className="w-full h-[400px] relative bg-card rounded-lg overflow-hidden">
      {/* Chart */}
      <div ref={chartContainerRef} className="w-full" style={{ height: '100%' }}>
        {containerWidth > 0 && (
          <BarChart
            width={containerWidth}
            height={350}
            data={chartData}
            margins={[40, 40, 20, period === DataPeriod.Monthly ? 60 : 40]}
            series={
              <BarSeries
                colorScheme={[CHART_COLORS.trackedTime.bar]}
                padding={0.1}
                binSize={24 * 60 * 60 * 1000} // 24 hours in milliseconds
                bar={<Bar rx={4} ry={4} />}
                tooltip={
                  <TooltipArea
                    tooltip={
                      <ChartTooltip
                        followCursor={true}
                        content={(data: any) => (
                          <div className="p-3 rounded-md shadow-lg bg-card border">
                            <div className="text-sm font-semibold text-gray-800 mb-2 border-b pb-1">
                              {formatXAxisTime(data.x)}
                            </div>
                            <div className="text-sm font-bold">{formatTooltipTime(data.y)}</div>
                          </div>
                        )}
                      />
                    }
                  />
                }
              />
            }
            gridlines={<GridlineSeries line={<Gridline strokeWidth={0.5} strokeDasharray="4 4" />} />}
            xAxis={
              <LinearXAxis
                type="time"
                domain={[startDate.getTime(), endDate.getTime()]}
                tickSeries={
                  <LinearXAxisTickSeries
                    label={
                      <LinearXAxisTickLabel
                        format={formatXAxisTime}
                        rotation={period === DataPeriod.Monthly ? -45 : 0}
                        textAnchor={period === DataPeriod.Monthly ? 'end' : 'middle'}
                        fontSize={11}
                        fill="#333"
                        padding={period === DataPeriod.Monthly ? 10 : 5}
                        align="center"
                        position="end"
                      />
                    }
                    tickSize={5}
                    tickValues={generateTimeMarks.map((date) => date.getTime())}
                  />
                }
                visibility="visible"
                axisLine={null}
              />
            }
            yAxis={
              <LinearYAxis
                type="value"
                axisLine={null}
                tickSeries={
                  <LinearYAxisTickSeries
                    label={<LinearYAxisTickLabel format={formatYAxisValue} fontSize={11} fill="#333" />}
                  />
                }
              />
            }
          />
        )}
      </div>
    </div>
  );
};
